import React, { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import { fetchInventoryInsights } from "../../actions/inventoryInsightAction";
import { formatIDR } from "../../utils/helpers/formatIDR";
import ItemAvatar from "../../components/ItemAvatar";
import AkasSelect from "../../components/AkasSelect";
import PATH_URL from "../../routers/path";
import { AKAS_INVENTORY_STATUS_COLORS } from "../../utils/constants/chartColors";
import useAkasChartColors from "../../hooks/useAkasChartColors";
import "./inventory-insight.css";

const statusConfig = {
  out_of_stock: { label: "Stok Habis", className: "bg-danger-lt text-danger", color: AKAS_INVENTORY_STATUS_COLORS.out_of_stock },
  critical: { label: "Kritis", className: "bg-warning-lt text-warning", color: AKAS_INVENTORY_STATUS_COLORS.critical },
  reorder_soon: { label: "Segera Restock", className: "bg-orange-lt text-orange", color: AKAS_INVENTORY_STATUS_COLORS.reorder_soon },
  overstock: { label: "Overstock", className: "bg-blue-lt text-blue", color: AKAS_INVENTORY_STATUS_COLORS.overstock },
  healthy: { label: "Aman", className: "bg-success-lt text-success", color: AKAS_INVENTORY_STATUS_COLORS.healthy },
};

const actionConfig = {
  purchase: { label: "Buat Pembelian", path: PATH_URL.PURCHASE },
  production: { label: "Produksi", path: PATH_URL.PRODUCT },
};

const itemTypeConfig = {
  material: "Material",
  product: "Produk",
};

const formatQty = (value) => {
  const numberValue = Number(value || 0);
  return Number.isInteger(numberValue) ? numberValue : numberValue.toFixed(2);
};

const InsightSkeletonRows = () => (
  <>
    {[...Array(6)].map((_, index) => (
      <tr key={`inventory-insight-skeleton-${index}`}>
        <td>
          <div className="placeholder placeholder-sm col-8"></div>
        </td>
        <td>
          <div className="placeholder placeholder-sm col-5"></div>
        </td>
        <td>
          <div className="placeholder placeholder-sm col-6"></div>
        </td>
        <td>
          <div className="placeholder placeholder-sm col-6"></div>
        </td>
        <td>
          <div className="placeholder placeholder-sm col-7"></div>
        </td>
        <td>
          <div className="placeholder placeholder-sm col-8"></div>
        </td>
        <td>
          <div className="placeholder placeholder-sm col-7"></div>
        </td>
      </tr>
    ))}
  </>
);

const KpiCard = ({ title, value, subtitle, color = "primary", isLoading = false }) => (
  <div className="col-6 col-lg-3">
    <div className={`card card-sm h-100 akas-summary-card inventory-intelligence-kpi tone-${color}`}>
      <div className="card-body">
        {isLoading ? (
          <div className="placeholder-glow" aria-label={`Memuat ${title}`}>
            <div className="placeholder placeholder-sm col-5"></div>
            <div className="mt-3">
              <div className="placeholder placeholder-lg col-7"></div>
            </div>
            <div className="mt-2">
              <div className="placeholder placeholder-sm col-9"></div>
            </div>
          </div>
        ) : (
          <>
            <div className="text-secondary fw-bold text-uppercase small">{title}</div>
            <div className={`h1 mb-1 mt-2 text-${color}`}>{value}</div>
            <div className="text-secondary">{subtitle}</div>
          </>
        )}
      </div>
    </div>
  </div>
);

const DonutChartSkeleton = () => (
  <div className="placeholder-glow d-flex flex-column align-items-center justify-content-center" style={{ height: 300 }}>
    <div className="placeholder rounded-circle" style={{ width: 190, height: 190 }}></div>
    <div className="d-flex justify-content-center gap-2 mt-4 w-100">
      {[18, 22, 16, 20].map((width, index) => (
        <div
          key={`inventory-donut-legend-${index}`}
          className="placeholder placeholder-sm"
          style={{ width: `${width}%` }}></div>
      ))}
    </div>
  </div>
);

const BarChartSkeleton = () => (
  <div
    className="placeholder-glow d-flex flex-column justify-content-center gap-3"
    style={{ height: 300 }}
    aria-label="Memuat grafik rekomendasi">
    {[72, 91, 64, 82, 55, 75].map((width, index) => (
      <div key={`inventory-bar-${index}`} className="d-flex align-items-center gap-3">
        <div className="placeholder placeholder-sm" style={{ width: "18%" }}></div>
        <div className="placeholder rounded-2" style={{ width: `${width}%`, height: 20 }}></div>
      </div>
    ))}
  </div>
);

const InventoryInsightPage = ({ data, isLoading, fetchInventoryInsights }) => {
  const [days, setDays] = useState(30);
  const [leadTimeDays, setLeadTimeDays] = useState(7);
  const chartColors = useAkasChartColors();
  const [typeFilter, setTypeFilter] = useState("material");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchInventoryInsights({
      days,
      lead_time_days: leadTimeDays,
    });
  }, [days, fetchInventoryInsights, leadTimeDays]);

  const summary = data?.summary || {};
  const allItems = useMemo(() => {
    const items = [...(data?.materials || []), ...(data?.products || [])];
    return items.sort((first, second) => Number(second.priority_score || 0) - Number(first.priority_score || 0));
  }, [data?.materials, data?.products]);

  const filteredItems = useMemo(() => {
    return allItems.filter((item) => {
      const matchesType = typeFilter === "all" || item.item_type === typeFilter;
      const matchesStatus = statusFilter === "all" || item.status === statusFilter;
      const searchValue = search.toLowerCase();
      const matchesSearch =
        !searchValue ||
        item.name?.toLowerCase().includes(searchValue) ||
        item.category?.toLowerCase().includes(searchValue) ||
        item.type?.toLowerCase().includes(searchValue);

      return matchesType && matchesStatus && matchesSearch;
    });
  }, [allItems, search, statusFilter, typeFilter]);

  const recommendations = data?.recommendations || [];
  const statusLabels = ["out_of_stock", "critical", "reorder_soon", "overstock", "healthy"];
  const statusChartSeries = statusLabels.map((status) => Number(summary?.[status] || 0));
  const statusChartOptions = {
    chart: { type: "donut", toolbar: { show: false } },
    labels: statusLabels.map((status) => statusConfig[status].label),
    colors: statusLabels.map((status) => statusConfig[status].color),
    legend: { position: "bottom" },
    stroke: { width: 0 },
    dataLabels: { enabled: false },
    plotOptions: {
      pie: {
        donut: {
          size: "72%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Total Item",
              formatter: () => String(summary?.total_items || 0),
            },
          },
        },
      },
    },
  };

  const recommendationChartOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { horizontal: true, borderRadius: 6 } },
    colors: [chartColors.moneyOut],
    dataLabels: { enabled: false },
    xaxis: {
      categories: recommendations.slice(0, 8).map((item) => item.name),
      labels: {
        formatter: (value) => formatQty(value),
      },
    },
    tooltip: {
      y: {
        formatter: (value, { dataPointIndex }) => {
          const item = recommendations[dataPointIndex];
          return `${formatQty(value)} ${item?.unit || ""}`;
        },
      },
    },
  };

  const recommendationSeries = [
    {
      name: "Rekomendasi",
      data: recommendations.slice(0, 8).map((item) => Number(item.recommended_qty || 0)),
    },
  ];

  return (
    <div className="page-wrapper inventory-intelligence-page">
      <div className="page-header d-print-none inventory-intelligence-header">
        <div className="container-xl">
          <div className="inventory-intelligence-hero">
            <div className="col">
              <div className="inventory-intelligence-eyebrow">AKAS INVENTORY INTELLIGENCE</div>
              <h1 className="page-title fs-1 text-capitalize">Inventory Pintar</h1>
              <p>Keputusan restock berdasarkan pergerakan aktual, batas aman, dan kebutuhan operasional.</p>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <div className="btn-list">
                <Link to={PATH_URL.STOCK_OPNAME} className="btn btn-outline-primary">
                  Stock Opname
                </Link>
                <Link to={PATH_URL.PURCHASE} className="btn btn-primary">
                  Pembelian
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <section className="inventory-intelligence-filter mb-3">
          <div className="row g-3">
            <div className="col-md-4 col-lg-3">
              <label className="form-label">Periode Analisis</label>
              <AkasSelect className="form-select" value={days} onChange={(event) => setDays(Number(event.target.value))}>
                <option value={7}>7 hari terakhir</option>
                <option value={30}>30 hari terakhir</option>
                <option value={60}>60 hari terakhir</option>
                <option value={90}>90 hari terakhir</option>
              </AkasSelect>
            </div>
            <div className="col-md-4 col-lg-3">
              <label className="form-label">Lead Time Restock</label>
              <AkasSelect
                className="form-select"
                value={leadTimeDays}
                onChange={(event) => setLeadTimeDays(Number(event.target.value))}>
                <option value={3}>3 hari</option>
                <option value={7}>7 hari</option>
                <option value={14}>14 hari</option>
                <option value={30}>30 hari</option>
              </AkasSelect>
            </div>
            <div className="col-md-4 col-lg-3">
              <label className="form-label">Tipe Item</label>
              <AkasSelect
                className="form-select"
                value={typeFilter}
                onChange={(event) => setTypeFilter(event.target.value)}>
                <option value="all">Semua</option>
                <option value="material">Material</option>
                <option value="product">Produk</option>
              </AkasSelect>
            </div>
            <div className="col-md-12 col-lg-3">
              <label className="form-label">Cari Item</label>
              <input
                type="search"
                className="form-control"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="nama, kategori, tipe..."
              />
            </div>
          </div>
          </section>

          {!isLoading && recommendations.length ? (
            <section className="inventory-intelligence-focus mb-3">
              <ItemAvatar
                name={recommendations[0]?.name}
                image={recommendations[0]?.image_path || recommendations[0]?.image}
                size="md"
                className="inventory-intelligence-focus-icon"
              />
              <div>
                <span>PRIORITAS HARI INI</span>
                <strong>{recommendations[0]?.name}</strong>
                <p>
                  Disarankan menambah {formatQty(recommendations[0]?.recommended_qty)} {recommendations[0]?.unit || ""}
                  {recommendations[0]?.estimated_cost
                    ? ` dengan estimasi kebutuhan ${formatIDR(recommendations[0].estimated_cost)}.`
                    : " agar stok kembali aman."}
                </p>
              </div>
              {actionConfig[recommendations[0]?.action] ? (
                <Link className="btn btn-primary" to={actionConfig[recommendations[0].action].path}>
                  {actionConfig[recommendations[0].action].label}
                </Link>
              ) : null}
            </section>
          ) : null}

          <div className="row g-3 mb-3">
            <KpiCard
              title="Butuh Aksi"
              value={summary?.recommendation_count || 0}
              subtitle="item perlu restock / produksi"
              color="primary"
              isLoading={isLoading}
            />
            <KpiCard
              title="Stok Habis"
              value={summary?.out_of_stock || 0}
              subtitle="item tidak tersedia"
              color="danger"
              isLoading={isLoading}
            />
            <KpiCard
              title="Stok Kritis"
              value={summary?.critical || 0}
              subtitle="stok di bawah minimum"
              color="warning"
              isLoading={isLoading}
            />
            <KpiCard
              title="Estimasi Budget"
              value={formatIDR(summary?.estimated_budget || 0)}
              subtitle="berdasarkan rekomendasi"
              color="primary"
              isLoading={isLoading}
            />
          </div>

          <div className="row g-3 mb-3">
            <div className="col-lg-4">
              <div className="card h-100 inventory-intelligence-card">
                <div className="card-header">
                  <div>
                    <h3 className="card-title mb-0">Kesehatan Stok</h3>
                    <p className="text-secondary m-0">Komposisi status inventory saat ini</p>
                  </div>
                </div>
                <div className="card-body">
                  {isLoading ? (
                    <DonutChartSkeleton />
                  ) : (
                    <Chart options={statusChartOptions} series={statusChartSeries} type="donut" height={300} />
                  )}
                </div>
              </div>
            </div>
            <div className="col-lg-8">
              <div className="card h-100 inventory-intelligence-card">
                <div className="card-header">
                  <div>
                    <h3 className="card-title mb-0">Top Rekomendasi Quantity</h3>
                    <p className="text-secondary m-0">Prioritas restock/produksi berdasarkan stok dan pergerakan</p>
                  </div>
                </div>
                <div className="card-body">
                  {isLoading ? (
                    <BarChartSkeleton />
                  ) : recommendations.length > 0 ? (
                    <Chart options={recommendationChartOptions} series={recommendationSeries} type="bar" height={300} />
                  ) : (
                    <div className="empty">
                      <p className="empty-title">Belum ada rekomendasi mendesak</p>
                      <p className="empty-subtitle text-secondary">Inventory terlihat aman untuk periode ini.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="card inventory-intelligence-card inventory-intelligence-table">
            <div className="card-header">
              <div>
                <h3 className="card-title mb-0">Daftar Insight Inventory</h3>
                <p className="text-secondary m-0">
                  Menampilkan stok, estimasi hari habis, dan rekomendasi aksi per item.
                </p>
              </div>
              <div className="card-actions">
                <AkasSelect
                  className="form-select form-select-sm"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}>
                  <option value="all">Semua status</option>
                  {Object.entries(statusConfig).map(([value, config]) => (
                    <option value={value} key={value}>
                      {config.label}
                    </option>
                  ))}
                </AkasSelect>
              </div>
            </div>
            <div className="table-responsive">
              <table className="table table-vcenter card-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Status</th>
                    <th>Stok</th>
                    <th>Pemakaian / Hari</th>
                    <th>Hari Habis</th>
                    <th>Rekomendasi</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody className={isLoading ? "placeholder-glow" : ""}>
                  {isLoading ? (
                    <InsightSkeletonRows />
                  ) : filteredItems.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="text-center text-secondary py-5">
                        Tidak ada data inventory sesuai filter.
                      </td>
                    </tr>
                  ) : (
                    filteredItems.map((item) => {
                      const config = statusConfig[item.status] || statusConfig.healthy;
                      const action = actionConfig[item.action];

                      return (
                        <tr key={`${item.item_type}-${item.id}`}>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <ItemAvatar name={item.name} image={item?.image_path || item?.image} size="md" />
                              <div>
                                <div className="fw-bold">{item.name}</div>
                                <div className="text-secondary">
                                  {itemTypeConfig[item.item_type]} {item.category ? `• ${item.category}` : ""}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className={`badge fw-bold ${config.className}`}>{config.label}</span>
                          </td>
                          <td>
                            <div className="fw-bold">
                              {formatQty(item.stock)} {item.unit}
                            </div>
                            <div className="text-secondary">
                              Min {formatQty(item.min_stock)} {item.unit}
                            </div>
                          </td>
                          <td>
                            {formatQty(item.avg_daily_usage)} {item.unit}
                          </td>
                          <td>
                            {item.days_until_empty === null ? (
                              <span className="text-secondary">Belum ada pola</span>
                            ) : (
                              <span className={item.days_until_empty <= leadTimeDays ? "text-danger fw-bold" : ""}>
                                {formatQty(item.days_until_empty)} hari
                              </span>
                            )}
                          </td>
                          <td>
                            <div className="fw-bold">
                              {formatQty(item.recommended_qty)} {item.unit}
                            </div>
                            <div className="text-secondary">{formatIDR(item.estimated_cost || 0)}</div>
                          </td>
                          <td>
                            {action && item.recommended_qty > 0 ? (
                              <Link to={action.path} className="btn btn-sm btn-primary">
                                {action.label}
                              </Link>
                            ) : (
                              <span className="text-secondary">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  data: state?.inventoryInsights?.datas,
  isLoading: state?.inventoryInsights?.loading,
});

export default connect(mapStateToProps, {
  fetchInventoryInsights,
})(InventoryInsightPage);
